package link.erpango.quip.ui

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.*
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Email
import androidx.compose.material.icons.filled.Lock
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import link.erpango.quip.core.SharedFactory
import link.erpango.quip.core.auth.google.GoogleAuthProvider
import link.erpango.quip.theme.QuipColors
import androidx.compose.foundation.background
import org.jetbrains.compose.resources.painterResource
import link.erpango.quip.ui.components.QuipDividerWithText
import link.erpango.quip.ui.components.QuipPrimaryButton
import link.erpango.quip.ui.components.QuipSecondaryButton
import link.erpango.quip.ui.components.QuipTextField
import org.jetbrains.compose.resources.stringResource
import quip.composeapp.generated.resources.*

/**
 * Pantalla de login mejorada con diseño moderno
 */
@Composable
fun LoginScreen(
    sharedFactory: SharedFactory,
    googleAuthProvider: GoogleAuthProvider,
    onLoginSuccess: () -> Unit
) {
    val viewModel = sharedFactory.auth.createAuthViewModel().apply {
        checkCurrentSession()
    }

    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var name by remember { mutableStateOf("") }
    var isRegisterMode by remember { mutableStateOf(false) }
    
    val loginError by viewModel.loginError.collectAsState()
    val isLoading by viewModel.isLoading.collectAsState()
    val hasCredentials by viewModel.hasCredentials.collectAsState()
    val scaffoldState = rememberScaffoldState()

    LaunchedEffect(loginError) {
        loginError?.let {
            scaffoldState.snackbarHostState.showSnackbar(it)
        }
    }

    LaunchedEffect(hasCredentials) {
        if (hasCredentials == true) {
            onLoginSuccess()
        }
    }

    Scaffold(
        scaffoldState = scaffoldState,
        backgroundColor = QuipColors.colorBackground
    ) { innerPadding ->
        Box(modifier = Modifier.fillMaxSize().padding(innerPadding)) {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .imePadding()
                    .padding(24.dp)
                    .verticalScroll(rememberScrollState()),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                Image(
                    painter = painterResource(Res.drawable.ic_logo),
                    contentDescription = "Quip logo",
                    modifier = Modifier.size(96.dp)
                )

                Spacer(modifier = Modifier.height(24.dp))

                // Título
                Text(
                    text = if (isRegisterMode) stringResource(Res.string.login_create_account) else stringResource(Res.string.login_welcome),
                    style = MaterialTheme.typography.h4,
                    fontWeight = FontWeight.Bold,
                    color = QuipColors.colorPrimary
                )

                Spacer(modifier = Modifier.height(8.dp))

                Text(
                    text = if (isRegisterMode) stringResource(Res.string.login_sign_up_subtitle) else stringResource(Res.string.login_subtitle),
                    style = MaterialTheme.typography.body1,
                    color = QuipColors.colorText,
                    textAlign = TextAlign.Center
                )

                Spacer(modifier = Modifier.height(40.dp))

                if (isRegisterMode) {
                    QuipTextField(
                        value = name,
                        onValueChange = { name = it },
                        label = stringResource(Res.string.login_name_label),
                        icon = Icons.Default.Check // Using check as placeholder, maybe create Person icon?
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                }

                // Campo de email con icono
                QuipTextField(
                    value = email,
                    onValueChange = { email = it },
                    label = stringResource(Res.string.login_email),
                    icon = Icons.Default.Email
                )

                Spacer(modifier = Modifier.height(16.dp))

                // Campo de contraseña con icono
                OutlinedTextField(
                    value = password,
                    onValueChange = { password = it },
                    label = { Text(stringResource(Res.string.login_password)) },
                    modifier = Modifier.fillMaxWidth(),
                    leadingIcon = {
                        Icon(
                            imageVector = Icons.Default.Lock,
                            contentDescription = null,
                            tint = QuipColors.colorPrimary
                        )
                    },
                    colors = TextFieldDefaults.outlinedTextFieldColors(
                        focusedBorderColor = QuipColors.colorPrimary,
                        focusedLabelColor = QuipColors.colorPrimary,
                        cursorColor = QuipColors.colorPrimary,
                        textColor = Color.Black,
                        unfocusedBorderColor = QuipColors.colorText.copy(alpha = 0.5f),
                        unfocusedLabelColor = QuipColors.colorText
                    ),
                    shape = androidx.compose.foundation.shape.RoundedCornerShape(12.dp),
                    visualTransformation = PasswordVisualTransformation()
                )

                Spacer(modifier = Modifier.height(24.dp))

                // Botón principal de login
                QuipPrimaryButton(
                    text = if (isRegisterMode) stringResource(Res.string.login_sign_up_button) else stringResource(Res.string.login_sign_in),
                    onClick = {
                        if (isRegisterMode) {
                            if (name.isNotBlank() && email.isNotBlank() && password.isNotBlank()) {
                                viewModel.register(name, email, password)
                            }
                        } else {
                            viewModel.signIn(email, password)
                        }
                    },
                    icon = Icons.Default.Check
                )
                
                Spacer(modifier = Modifier.height(16.dp))
                
                // Toggle Login/Register
                TextButton(onClick = { isRegisterMode = !isRegisterMode }) {
                    Text(
                        text = if (isRegisterMode) stringResource(Res.string.login_already_have_account) else stringResource(Res.string.login_dont_have_account),
                        color = QuipColors.colorPrimary
                    )
                }

                Spacer(modifier = Modifier.height(24.dp))

                // Divider con texto
                QuipDividerWithText(text = stringResource(Res.string.login_or_continue_with))

                Spacer(modifier = Modifier.height(24.dp))

                // Botón de Google
                GoogleButtonUiContainer(
                    onGoogleSignInResult = { googleUser ->
                        googleUser?.idToken?.let {
                            viewModel.clearUserAuthData()
                            viewModel.loginWithGoogle(it) {
                                onLoginSuccess()
                            }
                        }
                    },
                    googleAuthProvider = googleAuthProvider,
                    content = {
                        QuipSecondaryButton(
                            text = stringResource(Res.string.login_sign_in_google),
                            onClick = { this.onClick() }
                        )
                    },
                    modifier = Modifier.fillMaxWidth()
                )

                Spacer(modifier = Modifier.height(16.dp))


            }
            
            if (isLoading) {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .background(Color.Black.copy(alpha = 0.5f)),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator(color = QuipColors.colorPrimary)
                }
            }
        }
    }
}